import Long from 'long';
import urlRegex from 'url-regex';

import {IMCTextEntities, MCTextEntities} from '../../../api/proto';
import stringToUint8Array from '../../../utils/arrayUtils/stringToUint8Array';
import emailRegex from '../../../utils/regex/emailRegex';

const urlPattern = urlRegex({strict: false});

const emailPattern = emailRegex();

const phonePattern = new RegExp(
  '\\+(?:(\\d{1,4})[-.\\s]?)?(?:[(](\\d{1,3})[)][-.\\s]?)?(\\d{1,4})[-.\\s]?(\\d{1,4})[-.\\s]?(\\d{1,9})',
  'g',
);

function hasSpaceAfter(substring, string) {
  const position = string.indexOf(substring);

  if (position !== -1 && position + substring.length < string.length) {
    return string.charAt(position + substring.length) === ' ';
  }
  return false;
}

export const findUrls = (text?: string | null): IMCTextEntities[] => {
  const textEntities: IMCTextEntities[] = [];
  const txt = text || '';
  let match;

  do {
    match = urlPattern.exec(txt);
    if (match) {
      textEntities.push({
        type: MCTextEntities.Type.LINK,
        offset: Long.fromNumber(match.index),
        length: Long.fromNumber(match[0].length),
      });
    }
  } while (match);

  return textEntities;
};

export const findEmails = (text?: string | null): IMCTextEntities[] => {
  const newEntities: IMCTextEntities[] = [];

  if (text) {
    let match: RegExpExecArray | never[] | null = [];

    while ((match = emailPattern.exec(text))) {
      newEntities.push({
        type: MCTextEntities.Type.EMAIL_ADDRESS,
        offset: Long.fromNumber(match.index || 0),
        length: Long.fromNumber(match[0]?.length),
        payload: stringToUint8Array(match[0]),
      });
    }
  }

  return newEntities;
};

export function findEmailsOrUrls(text?: string | null): IMCTextEntities[] {
  if (!text) {
    return [];
  }

  const newEntities: IMCTextEntities[] = [];
  const regexUrlEmail = new RegExp(`(${urlPattern.source})|(${emailPattern.source})`, 'g');

  let match: RegExpExecArray | null;
  while ((match = regexUrlEmail.exec(text))) {
    let matchedText = match[0];
    if (matchedText.endsWith('.')) {
      matchedText = matchedText.slice(0, -1);
    }

    const type = emailPattern.test(matchedText) ? MCTextEntities.Type.EMAIL_ADDRESS : MCTextEntities.Type.LINK;

    newEntities.push({
      type,
      offset: Long.fromNumber(match.index || 0),
      length: Long.fromNumber(matchedText.length),
      payload: stringToUint8Array(matchedText),
    });
  }

  return newEntities;
}

export const findPhones = (text?: string | null): IMCTextEntities[] => {
  const newEntities: IMCTextEntities[] = [];

  if (text) {
    let match: RegExpExecArray | never[] | null = [];

    while ((match = phonePattern.exec(text))) {
      if (text.length === match[0]?.length || hasSpaceAfter(match[0], text)) {
        newEntities.push({
          type: MCTextEntities.Type.PHONE_NUMBER,
          offset: Long.fromNumber(match.index || 0),
          length: Long.fromNumber(match[0]?.length),
          payload: stringToUint8Array(match[0]),
        });
      }
    }
  }

  return newEntities;
};

export const filterUnique = (
  textEntities1: IMCTextEntities[],
  textEntities2: IMCTextEntities[],
  skipType?: boolean,
): IMCTextEntities[] => {
  // Build a hash set of keys from textEntities2 for O(1) lookups.
  // Key format depends on skipType:
  //  - skipType=false => include type in key
  //  - skipType=true  => ignore type (match only by offset and length)
  const keyWithoutType = (e: IMCTextEntities): string => {
    const offset = (e.offset || Long.ZERO).toString();
    const length = (e.length || Long.ZERO).toString();
    return `${offset}#${length}`;
  };

  const keyWithType = (e: IMCTextEntities): string => {
    const type = String((e as any).type); // keep strict equality semantics for undefined as well
    const offset = (e.offset || Long.ZERO).toString();
    const length = (e.length || Long.ZERO).toString();
    return `${type}#${offset}#${length}`;
  };

  const makeKey = skipType ? keyWithoutType : keyWithType;

  const seen = new Set<string>();
  for (const te of textEntities2) {
    seen.add(makeKey(te));
  }

  const result: IMCTextEntities[] = [];
  for (const te of textEntities1) {
    if (!seen.has(makeKey(te))) {
      result.push(te);
    }
  }

  return result;
};
